// Bloco "Agendamento de Test Drive" (ATW-417). Exposto via Module Federation.
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import type { GroupableUnit } from "@autodromo/units"
import { useFormUnits } from "../../hooks/useFormUnits"
import { VehicleMedia } from "../../components/VehicleMedia/VehicleMedia"
import { interpolate, type DynamicStructure } from "../../utils/interpolate"
import * as S from "./AgendamentoTestDrive.styles"
import { mapEntries, mapUnits } from "./mapEntries"
import { SearchSelect } from "./parts/SearchSelect"
import { MonthCalendar } from "./parts/MonthCalendar"
import {
  ContactFields,
  validateContactFields,
  type ContactFieldError,
} from "./parts/ContactFields"
import { SummaryCard } from "./parts/SummaryCard"
import { Stepper } from "./parts/Stepper"
import { DefaultVehiclePlaceholder } from "./parts/DefaultVehiclePlaceholder"
import { Confirmation } from "./parts/Confirmation"
import {
  useAvailability,
  useAvailableDays,
  useUnitsAvailability,
} from "./useAvailability"
import {
  openDaysInRange,
  slotsForDate,
  unitAvailabilitySets,
  toISODate,
} from "./availabilityFromConfig"
import { useQueryParamsAdapter } from "../../hooks/queryParams/queryParamsAdapter"
import { submitTestDrive } from "./submit"
import { useAntiBot, ANTIBOT_ACTIONS } from "../../hooks/useAntiBot"
import { getOrCreateVisitorId } from "../../utils/visitorId"
import {
  openExternalTab,
  reserveExternalTab,
  type ReservedExternalTab,
} from "../../utils/externalTab"
import { isSafariUserAgent } from "../../utils/userAgent"
import type {
  AgendamentoTestDriveProps,
  ContactValues,
  VehicleImageSource,
  WizardStep,
} from "./types"

const EMPTY_CONTACT: ContactValues = {
  name: "",
  email: "",
  whatsapp: "",
  cpf: "",
  preferences: [],
  consent: false,
}

export const STEPS: {
  key: WizardStep
  label: string
  name: string
  title: string
  subtitle?: string
  titleFieldPath?: keyof AgendamentoTestDriveProps
  subtitleFieldPath?: keyof AgendamentoTestDriveProps
}[] = [
  {
    key: "modelo",
    label: "MODELO",
    name: "Modelo",
    title: "Escolha um modelo",
    subtitle: "Selecione o modelo, a versão e a unidade de sua preferência.",
    titleFieldPath: "modeloTitle",
    subtitleFieldPath: "modeloSubtitle",
  },
  {
    key: "dataHora",
    label: "DATA E HORA",
    name: "Data e hora",
    title: "Escolha a data e o horário",
    subtitle: "Escolha a data e o horário de sua preferência.",
    titleFieldPath: "dataHoraTitle",
    subtitleFieldPath: "dataHoraSubtitle",
  },
  {
    key: "dados",
    label: "DADOS PESSOAIS",
    name: "Dados pessoais",
    title: "Preencha seus dados",
    subtitle: "Informe seus dados para receber a confirmação do seu test drive.",
    titleFieldPath: "dadosTitle",
    subtitleFieldPath: "dadosSubtitle",
  },
  {
    key: "resumo",
    label: "RESUMO",
    name: "Resumo",
    title: "Confira seu agendamento",
  },
]

const WEEKDAYS_FULL = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
]
const MONTHS_FULL = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
]
const pad = (n: number) => String(n).padStart(2, "0")

/** 2026-09-08 → 08/09/2026 */
const formatShortDate = (iso: string): string =>
  iso ? iso.split("-").reverse().join("/") : ""

/** "Terça-feira, 8 de setembro" a partir de YYYY-MM-DD. */
const formatDayLabel = (isoDate: string): string => {
  const d = new Date(`${isoDate}T00:00:00`)
  return `${WEEKDAYS_FULL[d.getDay()]}, ${d.getDate()} de ${MONTHS_FULL[d.getMonth()]}`
}

/** Hoje em YYYY-MM-DD (piso do calendário — o prazo mínimo real, inclusive "Mesmo dia",
    vem do backend via enabledDays). */
const todayISO = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Duração real derivada dos próprios chips (o menor intervalo entre horários adjacentes =
// a duração configurada na conta). O rodapé/confirmação não têm a config no site.
export const slotIntervalMinutes = (slots: string[]): number | null => {
  if (slots.length < 2) return null
  const toMin = (t: string): number => {
    const [h, m] = t.split(":").map(Number)
    return (h || 0) * 60 + (m || 0)
  }
  const mins = slots.map(toMin).sort((a, b) => a - b)
  let min = Infinity
  for (let i = 1; i < mins.length; i++) {
    const diff = mins[i] - mins[i - 1]
    if (diff > 0 && diff < min) min = diff
  }
  return Number.isFinite(min) ? min : null
}

const AgendamentoTestDriveInner = (
  {
    htmlId,
    title = "Agende seu Test Drive",
    titleTag = "h2",
    titleVisible = true,
    description = "Escolha o modelo, a unidade e o melhor horário para você.",
    descriptionVisible = true,
    palette,
    activePalette,
    padding,
    backgroundType,
    bgColor,
    backgroundImage,
    backgroundImageAlt,
    blockTitleColor,
    blockDescColor,
    stepTitleColor,
    stepSubtitleColor,
    noticeTitleColor,
    fieldLabelColor,
    fieldTextColor,
    fieldPlaceholderColor,
    fieldBgColor,
    fieldBorderColor,
    cardBgColor,
    cardBorderColor,
    stepCurrentColor,
    stepCompletedColor,
    stepPendingColor,
    buttonBgColor,
    buttonTextColor,
    backButtonTextColor,
    layoutFormat = "vitrine",
    previewContainer = false,
    previewPadding,
    stepStyle = "trilha",
    headerAlign = "esquerda",
    submitEndpoint,
    product,
    submitErrorMessage = "Não foi possível enviar seu pedido. Tente novamente em instantes.",
    afterRedirect,
    modeloTitle,
    modeloSubtitle,
    dataHoraTitle,
    dataHoraSubtitle,
    dadosTitle,
    dadosSubtitle,
    modeloTitleTag = "h3",
    dataHoraTitleTag = "h3",
    dadosTitleTag = "h3",
    vehicleImagePrimary = "gallery360",
    vehicleImageFallback = "mainImage",
    rotationHintText = "Arraste para girar",
    placeholderType = "default",
    placeholderImage,
    catalogSource = "modelo",
    zeroKmOnly = true,
    dynamicStructure,
    accountId,
    siteId,
    antiBot,
    slotDurationMinutes = 45,
    showCpf = true,
    requireCpf = true,
    hideOnMobile,
    hideOnDesktop,
    useGlobalQueryParams,
    loading = false,
    forceNoAvailability = false,
    availabilityConfig,
  }: AgendamentoTestDriveProps,
  ref: React.Ref<HTMLDivElement>,
) => {
  const [step, setStep] = useState<WizardStep>("modelo")
  const [unitId, setUnitId] = useState("")
  const [modelId, setModelId] = useState("")
  const [versionId, setVersionId] = useState("")
  const [date, setDate] = useState("")
  const [timeSlot, setTimeSlot] = useState("")
  const [contact, setContact] = useState<ContactValues>(EMPTY_CONTACT)
  const [protocol, setProtocol] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [contactErrors, setContactErrors] = useState<ContactFieldError[]>([])

  const { execute: executeAntiBot } = useAntiBot(antiBot)

  const { slots: fetchedSlots, loading: fetchedSlotsLoading } = useAvailability(
    accountId,
    unitId || undefined,
    date || undefined,
  )
  // Builder: a config injetada gera os horários localmente (o crm não responde no editor).
  const localSlots = useMemo(
    () =>
      availabilityConfig && unitId && date
        ? slotsForDate(availabilityConfig, unitId, date)
        : [],
    [availabilityConfig, unitId, date],
  )
  const slots = availabilityConfig ? localSlots : fetchedSlots
  const slotsLoading = availabilityConfig ? false : fetchedSlotsLoading

  // Dias com atendimento do mês visível (backend) → desabilita os fechados no calendário.
  const [monthRange, setMonthRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  })
  const onMonthChange = useCallback(
    (from: string, to: string) => setMonthRange({ from, to }),
    [],
  )
  const { days: fetchedOpenDays, loaded: fetchedDaysLoaded } = useAvailableDays(
    accountId,
    unitId || undefined,
    monthRange.from || undefined,
    monthRange.to || undefined,
  )
  // Builder: dias abertos do mês vêm da config (mesma janela do crm, sem vagas).
  const localOpenDays = useMemo(
    () =>
      availabilityConfig && unitId && monthRange.from && monthRange.to
        ? new Set(
            openDaysInRange(
              availabilityConfig,
              unitId,
              monthRange.from,
              monthRange.to,
              toISODate(new Date()),
            ),
          )
        : new Set<string>(),
    [availabilityConfig, unitId, monthRange.from, monthRange.to],
  )
  const openDays = availabilityConfig ? localOpenDays : fetchedOpenDays
  const daysLoaded = availabilityConfig ? true : fetchedDaysLoaded

  const isSeminovos = catalogSource === "seminovos"
  // Rótulo da Etapa 1 conforme a fonte (Modelo × Seminovo).
  const vehicleLabel = isSeminovos ? "Seminovo" : "Modelo"

  // Interpolação dos tokens {{channel.*}} (só dados do Site) no título/descrição/etapas.
  // O runtime traz o channelData completo; o tipo do bloco é só mais estreito.
  const interpolationStructure = (dynamicStructure ??
    {}) as unknown as DynamicStructure

  const { vehicles: allVehicles } = useMemo(
    () => mapEntries(dynamicStructure),
    [dynamicStructure],
  )
  // Seminovos com "Somente Zero KM" ligado → exibe apenas os marcados `zero-km`.
  const vehicles = useMemo(
    () =>
      isSeminovos && zeroKmOnly ? allVehicles.filter((v) => v.zeroKm) : allVehicles,
    [allVehicles, isSeminovos, zeroKmOnly],
  )

  // Só unidade ativa no site (unit.active) e habilitada no Test Drive (testDriveEnabled)
  // entra no fluxo. Ausência do campo (site antigo, pré-republish) = não esconde (ATW-417).
  const availableChannelUnits = useMemo(
    () =>
      ((dynamicStructure?.channel?.units ?? []) as GroupableUnit[]).filter((u) => {
        const unit = u as { active?: boolean; testDriveEnabled?: boolean }
        return unit.active !== false && unit.testDriveEnabled !== false
      }),
    [dynamicStructure],
  )

  // Unidades: cascata registro → região → canal (fonte única, per CLAUDE do blocks).
  const { units: allowedUnits } = useFormUnits<GroupableUnit>({
    channelUnits: availableChannelUnits,
    unitGroupingMode: dynamicStructure?.channel
      ?.unitGroupingMode as Parameters<typeof useFormUnits>[0]["unitGroupingMode"],
    unitGroups: dynamicStructure?.channel
      ?.unitGroups as Parameters<typeof useFormUnits>[0]["unitGroups"],
    entry: dynamicStructure?.item,
    useGlobalQueryParams,
  })
  const units = useMemo(() => mapUnits(allowedUnits), [allowedUnits])

  // Disponibilidade por unidade (janela de 60 dias) → estados bloqueados (ATW-417):
  // nenhuma unidade aberta = aviso na Etapa 1; unidade escolhida fechada = aviso na Etapa 2.
  const unitIds = useMemo(() => units.map((u) => u.id), [units])
  const {
    openUnitIds: fetchedOpenUnits,
    closedUnitIds: fetchedClosedUnits,
    loaded: fetchedAvailabilityLoaded,
  } = useUnitsAvailability(accountId, unitIds)
  // Builder: quais unidades têm agenda aberta sai da config (sem o crm).
  const localUnitSets = useMemo(
    () =>
      availabilityConfig
        ? unitAvailabilitySets(availabilityConfig, unitIds, toISODate(new Date()))
        : null,
    [availabilityConfig, unitIds],
  )
  const openUnitIds = localUnitSets ? localUnitSets.openUnitIds : fetchedOpenUnits
  const closedUnitIds = localUnitSets
    ? localUnitSets.closedUnitIds
    : fetchedClosedUnits
  const availabilityLoaded = availabilityConfig
    ? true
    : fetchedAvailabilityLoaded
  const noAvailability =
    forceNoAvailability ||
    (availabilityLoaded &&
      unitIds.length > 0 &&
      openUnitIds.size === 0 &&
      closedUnitIds.size === unitIds.length)
  const selectedUnitClosed = !!unitId && closedUnitIds.has(unitId)

  // Duração exibida = intervalo real dos chips; cai na prop só quando não dá pra derivar.
  const effectiveDurationMinutes = useMemo(
    () => slotIntervalMinutes(slots) ?? slotDurationMinutes,
    [slots, slotDurationMinutes],
  )

  const selectedModel = vehicles.find((m) => m.id === modelId) ?? null
  const versions = selectedModel?.versions ?? []
  const selectedVersion = versions.find((v) => v.id === versionId) ?? null
  const selectedUnit = units.find((u) => u.id === unitId) ?? null

  // Deep-link ?modelo=&versao= pré-seleciona a Etapa 1 (casa por slug ou UUID). Aplica
  // uma vez, quando o catálogo carrega; não sobrescreve a escolha do visitante.
  const { searchParams } = useQueryParamsAdapter()
  const deepLinkAppliedRef = useRef(false)
  useEffect(() => {
    if (deepLinkAppliedRef.current || vehicles.length === 0) return
    const modeloParam = searchParams.get("modelo")
    if (!modeloParam) {
      deepLinkAppliedRef.current = true
      return
    }
    const model = vehicles.find(
      (m) => m.id === modeloParam || m.uuid === modeloParam,
    )
    deepLinkAppliedRef.current = true
    if (!model) return
    setModelId(model.id)
    const versaoParam = searchParams.get("versao")
    if (versaoParam) {
      const version = model.versions.find(
        (v) => v.id === versaoParam || v.uuid === versaoParam,
      )
      if (version) setVersionId(version.id)
    }
  }, [vehicles, searchParams])

  const stepIndex = STEPS.findIndex((s) => s.key === step)

  // Cabeçalho por etapa: usa o texto configurado no builder, senão o default (STEPS).
  // A etapa 4 (resumo) é fixa. Vazio numa prop = herda o default.
  const stepHeaders: Record<
    WizardStep,
    { title: string; titleTag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6"; subtitle?: string }
  > = {
    modelo: {
      title: modeloTitle || STEPS[0].title,
      titleTag: modeloTitleTag,
      subtitle: modeloSubtitle ?? STEPS[0].subtitle,
    },
    dataHora: {
      title: dataHoraTitle || STEPS[1].title,
      titleTag: dataHoraTitleTag,
      subtitle: dataHoraSubtitle ?? STEPS[1].subtitle,
    },
    dados: {
      title: dadosTitle || STEPS[2].title,
      titleTag: dadosTitleTag,
      subtitle: dadosSubtitle ?? STEPS[2].subtitle,
    },
    resumo: { title: STEPS[3].title, subtitle: STEPS[3].subtitle },
    confirmacao: { title: "" },
  }
  const currentHeader = stepHeaders[step]

  // CPF só é exigido se estiver sendo exibido.
  const cpfRequired = showCpf && requireCpf

  // Versão só é obrigatória quando o veículo selecionado tem versões (Modelo);
  // Seminovos é coleção plana, sem versões.
  // A Etapa 3 (dados) NÃO trava o botão: clicar valida e mostra os erros nos campos
  // (critério do card). As etapas de seleção seguem gated (não há erro de campo a exibir).
  const canContinue =
    step === "modelo"
      ? !!unitId && !!modelId && (versions.length === 0 || !!versionId)
      : step === "dataHora"
        ? !!date && !!timeSlot
        : true

  // Mídia do veículo: a fonte (galeria 360 × imagem principal) e o fallback vêm do
  // builder (aba Conteúdo → Imagem do veículo).
  const cover = selectedVersion?.image || selectedModel?.image || ""
  const gallery360 =
    (selectedVersion?.frames360?.length ?? 0) > 1
      ? (selectedVersion?.frames360 ?? [])
      : (selectedModel?.frames360?.length ?? 0) > 1
        ? (selectedModel?.frames360 ?? [])
        : []
  const pickMedia = (
    source: VehicleImageSource,
  ): { frames: string[]; cover: string } | null => {
    if (source === "gallery360" && gallery360.length > 1) return { frames: gallery360, cover }
    // "Galeria": mesma galeria do 360, exibida estática (1ª imagem, sem giro).
    if (source === "gallery" && gallery360.length > 0) return { frames: [], cover: gallery360[0] }
    if (source === "mainImage" && cover) return { frames: [], cover }
    return null
  }
  const media =
    pickMedia(vehicleImagePrimary) ??
    pickMedia(vehicleImageFallback) ?? { frames: [], cover: "" }
  const previewFrames = media.frames
  const previewCover = media.cover

  // Placeholder do preview: imagem custom do builder ou a silhueta padrão. Usado tanto
  // quando nenhum modelo está selecionado quanto quando o modelo escolhido não tem imagem.
  const placeholderMedia =
    placeholderType === "custom" && placeholderImage ? (
      <S.PreviewImage src={placeholderImage} alt="" data-af-field="placeholderImage" />
    ) : (
      <DefaultVehiclePlaceholder />
    )

  const handleSubmit = async () => {
    setSubmitting(true)
    setSubmitError(false)
    // Aba nova só é permitida pelo Safari se reservada dentro do gesto, antes de qualquer
    // await (ver utils/externalTab). Reserva aqui e navega/cancela após o envio.
    const wantsNewTab = Boolean(
      afterRedirect?.active &&
        (afterRedirect.type || "external_url") === "external_url" &&
        afterRedirect.url &&
        afterRedirect.openInNewTab,
    )
    const reservedTab: ReservedExternalTab | null =
      wantsNewTab && isSafariUserAgent()
        ? reserveExternalTab({ placeholderMessage: "Redirecionando..." })
        : null
    try {
      const antiBotToken = await executeAntiBot(ANTIBOT_ACTIONS.FORM_SUBMIT)
      const result = await submitTestDrive({
        accountId,
        siteId,
        model: selectedModel,
        version: selectedVersion,
        unit: selectedUnit,
        date,
        timeSlot,
        contact,
        afVisitorId: getOrCreateVisitorId() ?? undefined,
        antiBotToken: antiBotToken || undefined,
        endpoint: submitEndpoint,
        product,
      })
      // Redirecionamento após envio (aba Avançado): Página do site / URL externo
      // (com opção de nova guia) / Rolar para um bloco — em vez da confirmação inline.
      if (afterRedirect?.active) {
        const redirectType = afterRedirect.type || "external_url"
        switch (redirectType) {
          case "page":
            if (afterRedirect.pageSlug) {
              const slug = afterRedirect.pageSlug.startsWith("/")
                ? afterRedirect.pageSlug
                : `/${afterRedirect.pageSlug}`
              window.location.href = slug
              return
            }
            break
          case "external_url":
            if (afterRedirect.url) {
              if (!afterRedirect.openInNewTab) {
                window.location.href = afterRedirect.url
                return
              }
              if (reservedTab) reservedTab.navigate(afterRedirect.url)
              else openExternalTab({ url: afterRedirect.url })
            }
            break
          case "scroll_to_block":
            if (afterRedirect.blockId) {
              if (afterRedirect.scrollPageSlug) {
                const scrollSlug = afterRedirect.scrollPageSlug.startsWith("/")
                  ? afterRedirect.scrollPageSlug
                  : `/${afterRedirect.scrollPageSlug}`
                window.location.href = `${scrollSlug}#${afterRedirect.blockId}`
              } else {
                document
                  .getElementById(afterRedirect.blockId)
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              return
            }
            break
        }
      }
      setProtocol(result.protocol ?? "")
      setStep("confirmacao")
    } catch {
      reservedTab?.cancel()
      setSubmitError(true)
    } finally {
      setSubmitting(false)
    }
  }

  const advance = () => {
    if (step === "dados") {
      const errs = validateContactFields(contact, cpfRequired)
      setContactErrors(errs)
      if (errs.length > 0) return
    }
    if (step === "resumo") {
      void handleSubmit()
      return
    }
    setStep(STEPS[stepIndex + 1].key)
  }

  return (
    <S.Section
      id={htmlId}
      ref={ref}
      data-testid="agendamento-test-drive"
      className={activePalette || undefined}
      $hideOnMobile={hideOnMobile}
      $hideOnDesktop={hideOnDesktop}
      style={{
        ...(palette?.bgColor && { backgroundColor: palette.bgColor }),
        ...(palette?.textColor && { color: palette.textColor }),
        // Overrides de cor por slot (aba Design) → CSS vars, sobrescrevem a paleta.
        ...(bgColor ? { "--general-background": bgColor } : {}),
        ...(blockTitleColor ? { "--td-block-title": blockTitleColor } : {}),
        ...(blockDescColor ? { "--td-block-desc": blockDescColor } : {}),
        ...(stepTitleColor ? { "--td-step-title": stepTitleColor } : {}),
        ...(stepSubtitleColor ? { "--td-step-subtitle": stepSubtitleColor } : {}),
        ...(noticeTitleColor ? { "--td-notice": noticeTitleColor } : {}),
        ...(fieldLabelColor ? { "--td-field-label": fieldLabelColor } : {}),
        ...(fieldTextColor ? { "--td-field-text": fieldTextColor } : {}),
        ...(fieldPlaceholderColor ? { "--td-field-placeholder": fieldPlaceholderColor } : {}),
        ...(fieldBgColor ? { "--td-input-bg": fieldBgColor } : {}),
        ...(fieldBorderColor ? { "--td-field-border": fieldBorderColor } : {}),
        ...(cardBgColor ? { "--cards-background": cardBgColor } : {}),
        ...(cardBorderColor ? { "--cards-border": cardBorderColor } : {}),
        ...(stepCurrentColor ? { "--td-step-current": stepCurrentColor } : {}),
        ...(stepCompletedColor ? { "--td-step-completed": stepCompletedColor } : {}),
        ...(stepPendingColor ? { "--td-step-pending": stepPendingColor } : {}),
        ...(buttonBgColor
          ? { "--buttons-background": buttonBgColor, "--buttons-border": buttonBgColor }
          : {}),
        ...(buttonTextColor ? { "--buttons-text": buttonTextColor } : {}),
        ...(backButtonTextColor ? { "--td-back-text": backButtonTextColor } : {}),
      } as React.CSSProperties}
    >
      {backgroundType === "image" && backgroundImage && (
        <S.BackgroundImage src={backgroundImage} alt={backgroundImageAlt || ""} />
      )}
      {step === "confirmacao" ? (
        <S.ConfirmationContainer data-anim-stagger>
          <Confirmation
            protocol={protocol}
            date={date}
            timeSlot={timeSlot}
            slotDurationMinutes={effectiveDurationMinutes}
            modelName={selectedModel?.name ?? ""}
            versionName={selectedVersion?.name}
            cover={previewCover || undefined}
            frames={previewFrames}
            contact={contact}
            unitName={selectedUnit?.name ?? ""}
            unitAddress={selectedUnit?.address}
            mapLink={selectedUnit?.mapLink}
          />
        </S.ConfirmationContainer>
      ) : (
        <S.Container $format={layoutFormat} $padding={padding} data-anim-stagger>
          <S.Intro $align={headerAlign}>
            {titleVisible && title ? (
              <S.Title as={titleTag} data-af-field="title">
                {interpolate({ structure: interpolationStructure, template: title }).node}
              </S.Title>
            ) : null}
            {descriptionVisible && description ? (
              <S.Description data-af-field="description">
                {interpolate({ structure: interpolationStructure, template: description }).node}
              </S.Description>
            ) : null}
          </S.Intro>

          <StepFrame format={layoutFormat}>
          {layoutFormat === "stepByStep" && (
            <S.StepperArea>
              <Stepper
                steps={STEPS.map((s) => ({ label: s.label, name: s.name }))}
                currentIndex={stepIndex}
                variant={stepStyle}
              />
            </S.StepperArea>
          )}

          <S.PreviewPane
            $hideOnMobile={layoutFormat === "stepByStep" && step !== "modelo"}
          >
            {/* O card (container) envolve SÓ a mídia; nome/versão/legenda ficam abaixo, fora. */}
            <S.PreviewCard $container={previewContainer} $padding={previewPadding}>
              {selectedModel && (previewCover || previewFrames.length) ? (
                <S.PreviewMedia>
                  <VehicleMedia
                    frames={previewFrames}
                    cover={previewCover || undefined}
                    alt={`${selectedModel.name}${selectedVersion ? ` ${selectedVersion.name}` : ""}`}
                    maxHeight={{ mobile: "14rem", desktop: "16rem" }}
                    resetSignal={`${selectedModel.id}-${selectedVersion?.id ?? ""}`}
                    hint={{
                      show: previewFrames.length > 1,
                      label: rotationHintText,
                      color: "var(--text-body, #64748b)",
                    }}
                  />
                </S.PreviewMedia>
              ) : (
                <S.PreviewMedia>{placeholderMedia}</S.PreviewMedia>
              )}
            </S.PreviewCard>

            {selectedModel ? (
              <>
                <S.VehicleName>{selectedModel.name}</S.VehicleName>
                {selectedVersion && (
                  <S.VehicleVersion>{selectedVersion.name}</S.VehicleVersion>
                )}
              </>
            ) : (
              <S.PreviewPlaceholder>
                Selecione um modelo para continuar
              </S.PreviewPlaceholder>
            )}
          </S.PreviewPane>

          <S.Card $format={layoutFormat}>
          {layoutFormat !== "stepByStep" && (
            <Stepper
              steps={STEPS.map((s) => ({ label: s.label, name: s.name }))}
              currentIndex={stepIndex}
              variant={stepStyle}
            />
          )}

          <S.StepHeader>
            <S.StepHeaderTitle as={currentHeader.titleTag} data-af-field={STEPS[stepIndex]?.titleFieldPath}>
              {interpolate({ structure: interpolationStructure, template: currentHeader.title }).node}
            </S.StepHeaderTitle>
            {currentHeader.subtitle && (
              <S.StepHeaderSubtitle data-af-field={STEPS[stepIndex]?.subtitleFieldPath}>
                {currentHeader.subtitle}
              </S.StepHeaderSubtitle>
            )}
          </S.StepHeader>

          <S.StepBody>
            {loading && <span>Carregando…</span>}

            {!loading && step === "modelo" && (
              noAvailability ? (
                <S.UnavailableNotice role="status">
                  <S.UnavailableTitle>Agendamento indisponível</S.UnavailableTitle>
                  <S.UnavailableBody>
                    Nenhuma unidade está com agenda de Test Drive aberta no
                    momento. Entre em contato com a loja para agendar.
                  </S.UnavailableBody>
                </S.UnavailableNotice>
              ) : (
                <>
                <S.Field>
                  <S.FieldLabel>Unidade</S.FieldLabel>
                  <SearchSelect
                    ariaLabel="Unidade"
                    value={unitId}
                    onChange={(v) => {
                      setUnitId(v)
                      // a agenda depende da unidade — invalida a seleção anterior
                      setDate("")
                      setTimeSlot("")
                    }}
                    placeholder="Selecione"
                    searchPlaceholder="Buscar por nome, cidade ou endereço"
                    options={units.map((u) => ({
                      value: u.id,
                      label: u.name,
                      description:
                        u.address ||
                        [u.city, u.state].filter(Boolean).join(" - ") ||
                        undefined,
                    }))}
                  />
                </S.Field>

                <S.Field>
                  <S.FieldLabel>{vehicleLabel}</S.FieldLabel>
                  <SearchSelect
                    ariaLabel={vehicleLabel}
                    value={modelId}
                    onChange={(v) => {
                      setModelId(v)
                      setVersionId("")
                    }}
                    placeholder="Selecione"
                    searchPlaceholder="Buscar por nome ou marca"
                    options={vehicles.map((m) => ({
                      value: m.id,
                      label: m.name,
                      image: m.image || undefined,
                    }))}
                  />
                </S.Field>

                {!!modelId && versions.length > 0 && (
                  <S.Field>
                    <S.FieldLabel>Versão</S.FieldLabel>
                    <SearchSelect
                      ariaLabel="Versão"
                      value={versionId}
                      onChange={setVersionId}
                      placeholder="Selecione"
                      options={versions.map((v) => ({ value: v.id, label: v.name }))}
                    />
                  </S.Field>
                )}
                </>
              )
            )}

            {!loading && step === "dataHora" && (
              selectedUnitClosed ? (
                <S.UnavailableNotice role="status">
                  <S.UnavailableTitle>Unidade sem agenda</S.UnavailableTitle>
                  <S.UnavailableBody>
                    Esta unidade não está com agenda de Test Drive aberta. Volte e
                    escolha outra unidade.
                  </S.UnavailableBody>
                </S.UnavailableNotice>
              ) : (
                <>
                <MonthCalendar
                  value={date || null}
                  minISO={todayISO()}
                  enabledDays={daysLoaded ? openDays : undefined}
                  onMonthChange={onMonthChange}
                  onChange={(d) => {
                    setDate(d)
                    setTimeSlot("")
                  }}
                />

                {date && (
                  <>
                    <S.DayLabel>{formatDayLabel(date)}</S.DayLabel>
                    {slotsLoading ? (
                      <S.EmptySlots>Carregando horários…</S.EmptySlots>
                    ) : slots.length ? (
                      <>
                        <S.ChipsGrid>
                          {slots.map((s) => (
                            <S.Chip
                              key={s}
                              type="button"
                              $selected={timeSlot === s}
                              onClick={() => setTimeSlot(s)}
                            >
                              {s}
                            </S.Chip>
                          ))}
                        </S.ChipsGrid>
                        <S.Duration>{`Duração aproximada: ${effectiveDurationMinutes} min`}</S.Duration>
                      </>
                    ) : (
                      <S.EmptySlots>Sem horários disponíveis neste dia.</S.EmptySlots>
                    )}
                  </>
                )}

                <S.InfoBox>
                  <span aria-hidden>ⓘ</span>
                  <span>
                    Este é um pré-agendamento: a data e o horário dependem da
                    confirmação da unidade.
                  </span>
                </S.InfoBox>
                </>
              )
            )}

            {!loading && step === "dados" && (
              <ContactFields
                values={contact}
                onChange={(patch) => {
                  const next = { ...contact, ...patch }
                  setContact(next)
                  // Já mostrou erro? Revalida ao vivo enquanto o visitante corrige.
                  if (contactErrors.length)
                    setContactErrors(validateContactFields(next, cpfRequired))
                }}
                showCpf={showCpf}
                requireCpf={cpfRequired}
                errors={contactErrors}
              />
            )}

            {!loading && step === "resumo" && (
              <>
                <SummaryCard
                  title="Veículo"
                  onEdit={() => setStep("modelo")}
                  rows={[
                    { label: vehicleLabel, value: selectedModel?.name ?? "" },
                    ...(selectedVersion
                      ? [{ label: "Versão", value: selectedVersion.name }]
                      : []),
                  ]}
                />
                <SummaryCard
                  title="Agendamento"
                  onEdit={() => setStep("dataHora")}
                  rows={[
                    { label: "Unidade", value: selectedUnit?.name ?? "" },
                    { label: "Data", value: formatShortDate(date) },
                    { label: "Horário", value: timeSlot },
                  ]}
                />
                <SummaryCard
                  title="Dados pessoais"
                  onEdit={() => setStep("dados")}
                  rows={[
                    { label: "Nome completo", value: contact.name },
                    { label: "E-mail", value: contact.email },
                    { label: "WhatsApp", value: contact.whatsapp },
                  ]}
                />
                {submitError && <S.SubmitError>{submitErrorMessage}</S.SubmitError>}
              </>
            )}

          </S.StepBody>

          <S.Nav>
            {stepIndex > 0 ? (
              <S.GhostButton
                type="button"
                onClick={() => setStep(STEPS[stepIndex - 1].key)}
              >
                Voltar
              </S.GhostButton>
            ) : (
              <span aria-hidden />
            )}
            <S.PrimaryButton
              type="button"
              disabled={!canContinue || submitting}
              onClick={advance}
            >
              {step === "resumo"
                ? submitting
                  ? "Enviando…"
                  : "Enviar"
                : "Continuar"}
            </S.PrimaryButton>
          </S.Nav>
        </S.Card>
          </StepFrame>
        </S.Container>
      )}
    </S.Section>
  )
}

// Passo a passo: um card único envolve stepper + veículo + formulário. No vitrine
// não há card geral (Fragment) — manter os itens como filhos diretos do grid/Container
// preserva as áreas do vitrine e a animação de entrada por item.
const StepFrame = ({
  format,
  children,
}: {
  format: "vitrine" | "stepByStep"
  children: React.ReactNode
}) =>
  format === "stepByStep" ? <S.StepCard>{children}</S.StepCard> : <>{children}</>

export const AgendamentoTestDrive = forwardRef(AgendamentoTestDriveInner)
